import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { formatDate } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import { listChannels, listTemplates, type TemplateView } from './api';

/**
 * The WhatsApp template catalogue (context.md sections 53-56).
 *
 * A template is the only way to reach a customer whose 24-hour window has closed, and Meta decides
 * whether one exists. The status column is therefore read-only here by design: a tenant who could
 * mark their own template approved would make the send path's check meaningless.
 */

const STATUS_TONE: Record<string, BadgeTone> = {
  APPROVED: 'positive',
  PENDING: 'warning',
  DRAFT: 'neutral',
  REJECTED: 'danger',
  PAUSED: 'warning',
  DISABLED: 'neutral',
};

export function TemplatesPage(): React.JSX.Element {
  const [channelId, setChannelId] = useState<string>('');

  const channels = useQuery({ queryKey: ['channels'], queryFn: listChannels });

  const templates = useQuery({
    queryKey: ['templates', channelId],
    queryFn: () => listTemplates(channelId || undefined),
  });

  const whatsapp = (channels.data?.channels ?? []).filter((channel) => channel.kind === 'WHATSAPP');

  const columns: Column<TemplateView>[] = [
    {
      key: 'name',
      header: t('templates.name'),
      render: (template) => (
        <span className="cell-stack">
          <span className="strong">{template.name}</span>
          <span className="muted small">{body(template)}</span>
        </span>
      ),
    },
    {
      key: 'language',
      header: t('templates.language'),
      render: (template) => <code>{template.language}</code>,
    },
    {
      key: 'category',
      header: t('templates.category'),
      secondary: true,
      render: (template) => <Badge tone="info">{t(`templates.cat.${template.category}`)}</Badge>,
    },
    {
      key: 'parameters',
      header: t('templates.parameters'),
      secondary: true,
      render: (template) => {
        const total = Object.values(template.parameters).reduce((sum, count) => sum + count, 0);

        return total === 0 ? t('templates.noParameters') : String(total);
      },
    },
    {
      key: 'status',
      header: t('templates.status'),
      render: (template) => (
        <span className="cell-stack">
          <Badge tone={STATUS_TONE[template.status] ?? 'neutral'}>
            {t(`templates.state.${template.status}`)}
          </Badge>
          {template.status_detail && <span className="muted small">{template.status_detail}</span>}
        </span>
      ),
    },
    {
      key: 'synced',
      header: t('templates.synced'),
      secondary: true,
      align: 'end',
      render: (template) =>
        template.synced_at ? formatDate(template.synced_at) : t('templates.neverSynced'),
    },
  ];

  return (
    <div className="view">
      <PageHeader title={t('nav.templates')} description={t('templates.description')} />

      <div className="toolbar">
        <select
          className="select"
          value={channelId}
          onChange={(event) => setChannelId(event.target.value)}
          aria-label={t('templates.filterChannel')}
        >
          <option value="">{t('templates.allChannels')}</option>
          {whatsapp.map((channel) => (
            <option key={channel.id} value={channel.id}>
              {channel.name}
            </option>
          ))}
        </select>
      </div>

      <Panel>
        <DataTable
          tableKey="templates"
          rows={templates.data?.data}
          columns={columns}
          rowKey={(template) => template.id}
          isLoading={templates.isLoading}
          error={templates.error}
          onRetry={() => void templates.refetch()}
          empty={{
            title: t('templates.emptyTitle'),
            description:
              whatsapp.length === 0 ? t('templates.noChannel') : t('templates.emptyBody'),
          }}
        />
      </Panel>
    </div>
  );
}

/** The body text, which is what anybody scanning the list is actually looking for. */
function body(template: TemplateView): string {
  const text = template.components.find((component) => component.type === 'BODY')?.text ?? '';

  return text.length > 90 ? `${text.slice(0, 90)}…` : text || '—';
}
